/* src/components/CategoryToolbar.js */
import React, { useEffect, useCallback } from "react";
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useData } from "../hooks/useData";
import CategorySelect from "./CategorySelect";
import { signCategories } from "./Categories";

const CategoryToolbar = ({ canvas }) => {
  const {
    setSelectedObject,
    signCategoryState,
    setSignCategoryState,
    attributes,
    setAttributes,
    attributeOptions,
    setAttributeOptions,
    attributeValues,
    setAttributeValues,
    setBasketItem,
    upsaleSignPrice,
    setUpsaleSignPrice,
    setSignCategorySizeState,
    noticesMessage,
    setNoticesMessage,
    setSnackbar,
    loading,
    basketItem,
    signCategorySizeState,
  } = useData();

  /* --------------------------------------------------------------
   *  UPDATE BASKET – mirrors addToBasket
   * ------------------------------------------------------------ */
  const updateBasketItem = useCallback(
    (category, values) => {
      if (!category || !values) return;

      const basketData = {
        name: category.name,
        productCode: category.productCode,
        width: values.sizes?.width ?? 24,
        height: values.sizes?.height ?? 24,
        gauges: values.gauges?.name ?? "",
        addOns: values.addOns ? [values.addOns.name] : [],
      };

      const selectedSize = values.sizes ?? { price: 0 };
      const selectedGauge = values.gauges ?? { price: 0 };
      const selectedAddOn = values.addOns ?? { price: 0 };

      const gaugePrice =
        selectedGauge.price && basketData.width && basketData.height
          ? Number(selectedGauge.price) * basketData.width * basketData.height
          : 0;

      const totalPrice =
        Number(selectedSize.price ?? 0) +
        gaugePrice +
        Number(selectedAddOn.price ?? 0);

      const formattedPrice = totalPrice.toFixed(2);

      console.log("[Price] CategoryToolbar", {
        basketData,
        totalPrice,
        formattedPrice,
        selectedAddOn,
      });

      if (
        JSON.stringify(basketData) !== JSON.stringify(basketItem) ||
        formattedPrice !== upsaleSignPrice
      ) {
        setBasketItem(basketData);
        setUpsaleSignPrice(formattedPrice);
      }
    },
    [basketItem, upsaleSignPrice, setBasketItem, setUpsaleSignPrice]
  );

  /* --------------------------------------------------------------
   *  INITIALISE ATTRIBUTES
   * ------------------------------------------------------------ */
  useEffect(() => {
    if (!signCategoryState.name) {
      const empty = {};
      if (JSON.stringify(attributes) !== "{}") setAttributes(empty);
      if (JSON.stringify(attributeOptions) !== "{}") setAttributeOptions(empty);
      if (JSON.stringify(attributeValues) !== "{}") setAttributeValues(empty);
      if (
        signCategorySizeState.width !== 24 ||
        signCategorySizeState.height !== 24
      )
        setSignCategorySizeState({ width: 24, height: 24 });
      if (Object.keys(basketItem).length > 0) setBasketItem({});
      if (upsaleSignPrice !== "0.00") setUpsaleSignPrice("0.00");
      return;
    }

    const category = signCategories.find(
      (c) => c.name === signCategoryState.name
    );
    if (!category) {
      setSnackbar({
        open: true,
        message: "Category not found",
        severity: "error",
      });
      return;
    }

    const attrs = {};
    const options = {};
    const values = { ...attributeValues };
    let changed = false;

    Object.entries(category.product_attributes ?? {}).forEach(([key, attr]) => {
      const prompt = attr.prompt ?? key;
      if (attrs[key] !== prompt) changed = true;
      attrs[key] = prompt;

      const opts = attr.options ?? [];
      if (JSON.stringify(options[key]) !== JSON.stringify(opts)) changed = true;
      options[key] = opts;

      const cur = values[key];
      const def = opts[0] ?? {};

      if (!cur || !opts.find((o) => o.code === cur.code)) {
        values[key] = def;
        changed = true;
      }
    });

    if (changed || JSON.stringify(attrs) !== JSON.stringify(attributes))
      setAttributes(attrs);
    if (changed || JSON.stringify(options) !== JSON.stringify(attributeOptions))
      setAttributeOptions(options);
    if (changed || JSON.stringify(values) !== JSON.stringify(attributeValues))
      setAttributeValues(values);

    const size = values.sizes ?? {};
    const newSize = { width: size.width ?? 24, height: size.height ?? 24 };
    if (
      newSize.width !== signCategorySizeState.width ||
      newSize.height !== signCategorySizeState.height
    ) {
      setSignCategorySizeState(newSize);
    }

    updateBasketItem(category, values);
  }, [
    signCategoryState.name,
    setAttributes,
    setAttributeOptions,
    setAttributeValues,
    setSignCategorySizeState,
    setBasketItem,
    setUpsaleSignPrice,
    setSnackbar,
    updateBasketItem,
  ]);

  /* --------------------------------------------------------------
   *  CATEGORY SELECT HANDLER
   * ------------------------------------------------------------ */
  const handleCategorySelect = useCallback(
    (category) => {
      if (!canvas) {
        setSnackbar({
          open: true,
          message: "Canvas not ready",
          severity: "error",
        });
        return;
      }
      canvas.discardActiveObject();
      setSelectedObject(null);
      setSignCategoryState({ name: category.name });
      setNoticesMessage("");
    },
    [
      canvas,
      setSelectedObject,
      setSignCategoryState,
      setNoticesMessage,
      setSnackbar,
    ]
  );

  /* --------------------------------------------------------------
   *  ATTRIBUTE CHANGE HANDLER – ALL SINGLE SELECT
   * ------------------------------------------------------------ */
  const handleAttributeChange = useCallback(
    (attrKey, event) => {
      const category = signCategories.find(
        (c) => c.name === signCategoryState.name
      );
      if (!category) return;

      const selectedCode = event.target.value;
      const selectedOption = (attributeOptions[attrKey] ?? []).find(
        (o) => o.code === selectedCode
      );
      if (!selectedOption) return;

      const newValues = { ...attributeValues, [attrKey]: selectedOption };

      if (attrKey === "sizes") {
        setSignCategorySizeState({
          width: selectedOption.width,
          height: selectedOption.height,
        });
      }

      setAttributeValues(newValues);
      updateBasketItem(category, newValues);
    },
    [
      signCategoryState.name,
      attributeOptions,
      attributeValues,
      setAttributeValues,
      setSignCategorySizeState,
      updateBasketItem,
    ]
  );

  /* --------------------------------------------------------------
   *  RENDER
   * ------------------------------------------------------------ */
  return (
    <Box sx={{ textAlign: "left", p: 2, position: "relative" }}>
      {loading && (
        <CircularProgress
          size={24}
          sx={{ position: "absolute", top: 16, right: 16 }}
        />
      )}

      <CategorySelect canvas={canvas} onCategorySelect={handleCategorySelect} />

      {Object.entries(attributes).map(([key, prompt]) => {
        const isReadOnly = attributeOptions[key]?.length === 1;

        const selectValue = attributeValues[key]?.code ?? "";

        console.log("[Render] Select", {
          key,
          selectValue,
          selectedOption: attributeValues[key],
        });

        return (
          <React.Fragment key={key}>
            <Divider sx={{ my: 2 }} />
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>{prompt}</InputLabel>

              <Select
                value={selectValue}
                label={prompt}
                onChange={(e) => {
                  console.log("[onChange]", { key, value: e.target.value });
                  handleAttributeChange(key, e);
                }}
                disabled={isReadOnly || loading}
                sx={{ width: "90%", minWidth: "200px" }}
              >
                {(attributeOptions[key] ?? []).map((opt) => (
                  <MenuItem key={opt.code} value={opt.code}>
                    {opt.name} (${Number(opt.price ?? 0).toFixed(2)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </React.Fragment>
        );
      })}

      <Divider sx={{ my: 2 }} />
    </Box>
  );
};

export default CategoryToolbar;
